import { RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Common/button';
import Modal from '@/components/Common/Modal';
import MarkdownDisplay from '@/components/Common/MarkdownDisplay';
import { DeletedUser, UserDeletionLog } from '@/types';
import userService from '@/services/userService';

interface DeletionLogModalProps {
  open: boolean;
  user: DeletedUser | null;
  onClose: () => void;
  onRestored: () => void;
}

const DeletionLogModal: React.FC<DeletionLogModalProps> = ({
  open,
  user,
  onClose,
  onRestored,
}) => {
  const { t } = useTranslation();
  const [log, setLog] = useState<UserDeletionLog | null>(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setLog(null);
      setLogError(null);
      setShowRestoreConfirm(false);
      setRestoreError(null);
      setLoadingLog(true);
      userService.getUserDeletionLog(user.id)
        .then(setLog)
        .catch(() => setLogError(t('admin.users.deletionLog.logLoadError')))
        .finally(() => setLoadingLog(false));
    }
  }, [open, user, t]);

  const handleRestore = async () => {
    if (!user) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      await userService.restoreUser(user.id);
      onRestored();
      onClose();
    } catch {
      setRestoreError(t('admin.users.restore.error'));
    } finally {
      setRestoring(false);
    }
  };

  if (!user) return null;

  const deletedAt = user.deleted_at
    ? new Date(user.deleted_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  return (
    <Modal
      open={open}
      title={t('admin.users.deletionLog.title')}
      onClose={onClose}
      className="max-w-3xl"
      hideDefaultFooter
    >
      {/* Metadata summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 text-sm">
        <div className="bg-muted rounded-md p-3">
          <p className="text-xs text-muted-foreground mb-1">
            {t('admin.users.deletedUsersTab.deletedAt')}
          </p>
          <p className="font-medium text-foreground">{deletedAt}</p>
        </div>
        <div className="bg-muted rounded-md p-3">
          <p className="text-xs text-muted-foreground mb-1">
            {t('admin.users.deletedUsersTab.deletedBy')}
          </p>
          <p className="font-medium text-foreground">{user.deleted_by_name || '—'}</p>
        </div>
        <div className="bg-muted rounded-md p-3">
          <p className="text-xs text-muted-foreground mb-1">
            {t('admin.users.deletedUsersTab.transferredTo')}
          </p>
          <p className="font-medium text-foreground">{user.content_transferred_to_name || '—'}</p>
        </div>
      </div>

      {/* Restore confirmation */}
      {showRestoreConfirm ? (
        <div className="mb-5 p-4 rounded-md border border-amber-500/30 bg-amber-500/10">
          <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
            {t('admin.users.restore.description', { name: user.name || user.email })}
          </p>
          {restoreError && (
            <p className="text-sm text-destructive mb-2">{restoreError}</p>
          )}
          <div className="flex gap-2">
            <Button
              onClick={handleRestore}
              disabled={restoring}
              className="bg-amber-600 hover:bg-amber-700 text-white text-sm"
            >
              {restoring ? '...' : t('admin.users.restore.confirmButton')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowRestoreConfirm(false)}
              disabled={restoring}
            >
              {t('global.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-5">
          <Button
            variant="outline"
            onClick={() => setShowRestoreConfirm(true)}
            className="flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            {t('admin.users.restore.title')}
          </Button>
        </div>
      )}

      {/* Deletion log content */}
      <div className="border-t border-border pt-4">
        <p className="text-xs text-muted-foreground mb-3">
          {t('admin.users.deletionLog.subtitle')}
        </p>
        {loadingLog && (
          <p className="text-sm text-muted-foreground">{t('global.loading', 'Loading...')}</p>
        )}
        {logError && (
          <p className="text-sm text-destructive">{logError}</p>
        )}
        {log && (
          <div className="bg-muted rounded-md p-4 max-h-96 overflow-y-auto text-xs font-mono">
            <MarkdownDisplay content={log.log} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end mt-4 border-t border-border pt-4">
        <Button variant="outline" onClick={onClose}>
          {t('global.close')}
        </Button>
      </div>
    </Modal>
  );
};

export default DeletionLogModal;
